import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, Copy, Download, X } from 'lucide-react';
import { PageHeader } from '@/ui/page-header';
import { Panel } from '@/ui/panel';
import { Button } from '@/ui/button';
import { Tooltip } from '@/ui/tooltip';
import { Badge } from '@/ui/badge';
import { MicroLabel } from '@/ui/micro-label';
import { StatusDot } from '@/ui/status-dot';
import { useProfile } from '@/app/session';
import { useVendorProfile, vendorProfileKey } from '@/lib/queries/vendor-profile';
import { ErrorState } from '@/ui/error-state';
import { api, ApiError } from '@/lib/api';

interface IntakeCredential {
  credential_id: string;
  /** Null for native AWS S3, where clients use the regional default endpoint. */
  endpoint: string | null;
  region: string;
  bucket: string;
  prefix: string;
  access_key_id: string;
  secret_access_key: string;
  session_token: string;
  expires_at: string;
  shown_once: true;
  dataset_id: string;
  dataset_name: string;
}

const DOC_TONE: Record<string, 'pass' | 'warn' | 'muted'> = {
  signed: 'pass',
  accepted: 'pass',
  awaiting_signature: 'warn',
  pending: 'warn',
  none: 'muted'
};

export function VendorProfile() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile('vendor');
  const { data, isError, error, refetch } = useVendorProfile();
  const [credential, setCredential] = useState<IntakeCredential | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [intakeError, setIntakeError] = useState('');

  const org = profile?.organization_name ?? profile?.name ?? '';
  const code = profile?.vendor_code ?? '';
  const unsigned = data?.compliance_documents.find(d => d.state === 'awaiting_signature');

  async function issueCredential() {
    setIssuing(true);
    setIntakeError('');
    try {
      const issued = await api<IntakeCredential>('/vendor-profile/intake-credentials', { method: 'POST' });
      setCredential(issued);
      await queryClient.invalidateQueries({ queryKey: vendorProfileKey });
    } catch (cause) {
      setIntakeError(cause instanceof ApiError ? cause.message : 'Could not issue intake credentials.');
    } finally {
      setIssuing(false);
    }
  }

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Profile"
        subtitle={[org, code, 'Declared capabilities drive engagement invitations'].filter(Boolean).join(' · ')}
      />

      {isError ? <ErrorState error={error} onRetry={() => void refetch()} title="Could not load your profile" /> : null}

      <div className="grid grid-cols-2 items-start gap-5 max-lg:grid-cols-1">
        <Panel title="Declared capabilities" subtitle="Vocab v1.3 · verified against your submissions">
          <dl className="grid gap-3">
            {data?.capabilities.map(row => (
              <div key={row.label} className="grid grid-cols-[92px_1fr] items-center gap-3">
                <MicroLabel as="dt" size="xs">
                  {row.label}
                </MicroLabel>
                <dd className="flex flex-wrap items-center gap-1.5">
                  {row.values.map(value => (
                    <Badge key={value.text} caps={false} tone={value.pending ? 'outline' : 'neutral'}>
                      {value.text}
                    </Badge>
                  ))}
                </dd>
              </div>
            ))}
          </dl>
          <button type="button" className="mt-5 cursor-pointer text-[13px] text-primary hover:underline">
            Edit capabilities · changes require Miraxis review
          </button>
        </Panel>

        <Panel title="Compliance documents" subtitle="Signed via platform">
          <ul className="grid gap-3">
            {data?.compliance_documents.map(doc => (
              <li key={doc.key} className="flex items-center justify-between gap-4">
                <StatusDot tone={DOC_TONE[doc.state] ?? 'muted'} label={doc.label} />
                <span className="font-mono text-[11px] text-muted">{doc.detail}</span>
              </li>
            ))}
          </ul>
          {unsigned ? (
            <Tooltip label="Document signing is not available in this build">
              <span className="mt-5 inline-block">
                <Button disabled aria-disabled className="cursor-not-allowed opacity-60">
                  Sign {unsigned.label}
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Panel>

        <Panel title="Intake destination" subtitle="S3 uploads · batch-scoped credentials">
          {data && !data.intake ? (
            <p className="rounded border border-dashed border-border px-4 py-3 text-[13px] leading-relaxed text-muted">
              No intake credentials yet. Register a batch, then generate a temporary key for its dedicated upload prefix.
            </p>
          ) : (
            <dl className="grid gap-2 rounded border border-border bg-inset px-4 py-3 font-mono text-[12px]">
              <Row label="bucket" value={data?.intake?.bucket} />
              <Row label="access" value={data?.intake?.access} />
              <Row label="rotated" value={data?.intake?.rotated} />
            </dl>
          )}
          <div className="mt-4 flex items-center gap-3">
            <Button size="sm" onClick={() => void issueCredential()} disabled={issuing}>
              {issuing ? 'Issuing…' : data?.intake ? 'Request rotation' : 'Generate credentials'}
            </Button>
            <span className="text-[11px] text-dim">Temporary key · newest open batch</span>
          </div>
          {intakeError ? <p className="mt-3 text-[12px] text-danger">{intakeError}</p> : null}
        </Panel>

        <Panel title="Contacts" subtitle="Who we reach for flags + SLAs">
          <ul className="grid gap-4">
            {data?.contacts.map(contact => (
              <li key={contact.email} className="grid gap-0.5">
                <span className="text-[13px] text-foreground">
                  {contact.name} · {contact.role}
                </span>
                <span className="font-mono text-[11px] text-dim">{contact.email}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
      <CredentialDialog credential={credential} onClose={() => setCredential(null)} />
    </div>
  );
}

function CredentialDialog({ credential, onClose }: { credential: IntakeCredential | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  if (!credential) return null;

  const document = JSON.stringify(
    {
      endpoint: credential.endpoint,
      region: credential.region,
      bucket: credential.bucket,
      prefix: credential.prefix,
      access_key_id: credential.access_key_id,
      secret_access_key: credential.secret_access_key,
      session_token: credential.session_token,
      expires_at: credential.expires_at
    },
    null,
    2
  );
  const filename = `miraxis-intake-${credential.dataset_name.replace(/[^a-z0-9_-]+/gi, '-').toLowerCase()}.json`;

  function download() {
    const url = URL.createObjectURL(new Blob([document], { type: 'application/json' }));
    const link = window.document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Dialog.Root open onOpenChange={open => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70" />
        <Dialog.Content className="fixed top-1/2 left-1/2 z-50 grid max-h-[90vh] w-[min(620px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded border border-border bg-card p-6 shadow-2xl focus:outline-none">
          <div className="pr-10">
            <Dialog.Title className="text-[18px] font-medium text-foreground">Save intake credentials</Dialog.Title>
            <Dialog.Description className="mt-1 text-[12px] leading-relaxed text-muted">
              These secrets are shown once. They upload only to <span className="font-mono">{credential.dataset_name}</span>{' '}
              and expire {new Date(credential.expires_at).toLocaleString()}.
            </Dialog.Description>
          </div>
          <Dialog.Close asChild>
            <button
              type="button"
              aria-label="Close"
              className="absolute top-4 right-4 cursor-pointer rounded p-1 text-muted hover:bg-raised hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </Dialog.Close>

          <div className="grid gap-3 rounded border border-border bg-inset p-4">
            <CredentialRow label="Bucket" value={credential.bucket} />
            <CredentialRow label="Access key" value={credential.access_key_id} />
            <CredentialRow label="Secret key" value={credential.secret_access_key} secret />
            <p className="text-[11px] leading-relaxed text-dim">
              Connection details, the batch prefix, and the required temporary session token are included in Copy JSON
              and Download once, but are not displayed on screen.
            </p>
          </div>

          <p className="rounded border border-warning/30 bg-inset px-3 py-2 text-[12px] leading-relaxed text-muted">
            Closing this window permanently removes the secret and session token from the page. Previously issued keys
            remain valid until their displayed expiry.
          </p>

          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() =>
                void navigator.clipboard.writeText(document).then(() => {
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1500);
                })
              }
            >
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
              {copied ? 'Copied' : 'Copy JSON'}
            </Button>
            <Button onClick={download}>
              <Download className="size-3.5" /> Download once
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CredentialRow({ label, value, secret = false }: { label: string; value: string; secret?: boolean }) {
  return (
    <div className="grid gap-1">
      <MicroLabel size="xs">{label}</MicroLabel>
      <div className="select-all break-all rounded border border-border bg-background px-3 py-2 font-mono text-[11px] text-foreground">
        {secret ? value : value || '—'}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string }) {
  return (
    <div className="grid grid-cols-[72px_1fr] gap-3">
      <dt className="text-dim">{label}:</dt>
      <dd className="text-muted">{value ?? '—'}</dd>
    </div>
  );
}
